import isEqual from 'lodash/isEqual'
import has from 'lodash/has'

export function isValueLoading(data, key) {
  return !has(data, key)
}

/**
 * Checks if a given value is considered "empty".
 *
 * A value is considered empty if it meets any of the following conditions:
 * - It is `null` or `undefined`.
 * - It is an empty string (`''`).
 * - It is `-1` (as a number or a string).
 * - It is an empty array (`[]`).
 * - It is an empty object (`{}`).
 *
 * @param {*} value - The value to check for emptiness.
 * @param {object} options - Optional configuration options.
 * @param {Array} options.allowedValues - An array of values that are considered non-empty.
 * @param {Array} options.notAllowedValues - An array of values that are considered empty.
 * @returns {boolean} - Returns `true` if the value is empty, otherwise `false`.
 */
export function isValueEmpty(value, { allowedValues = [], notAllowedValues = [] } = {}) {
  if (allowedValues.some(allowedValue => isEqual(allowedValue, value))) {
    return false
  }

  if (notAllowedValues.some(notAllowedValue => isEqual(notAllowedValue, value))) {
    return true
  }

  if (value === null || value === undefined) {
    return true
  }

  if (value === false) {
    return true
  }

  if (typeof value === 'string' && (value === '' || value === '-1')) {
    return true
  }

  if (typeof value === 'number' && (value === -1 || Number.isNaN(value))) {
    return true
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return true
    }

    if (value.every(item => isValueEmpty(item))) {
      return true
    }
  }

  if (typeof value === 'object') {
    if (Object.keys(value).length === 0) {
      return true
    }

    if (Object.values(value).every(item => isValueEmpty(item))) {
      return true
    }
  }

  return false
}
